import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { generateInvoice as generateInvoiceAPI } from '../utils/invoiceService';

const InvoiceGenerator = ({ sale, customer, onInvoiceGenerated, onError, onGoBack }) => {
    console.log('=== InvoiceGenerator - RECEIVED PROPS ===');
    console.log('sale:', JSON.stringify(sale, null, 2));
    console.log('sale.formaPago:', sale?.formaPago);
    console.log('customer:', JSON.stringify(customer, null, 2));
    
    const [generationState, setGenerationState] = useState('ready'); // ready, generating, uploading, completed, error
    const [progress, setProgress] = useState(0);
    const [currentStep, setCurrentStep] = useState('');
    const [retryCount, setRetryCount] = useState(0);
    const [generatedInvoice, setGeneratedInvoice] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [detailedError, setDetailedError] = useState(null);

    const maxRetries = 3;

    const generateInvoice = useCallback(async () => {
        // Prevent multiple simultaneous calls
        if (isGenerating) {
            console.log('Invoice generation already in progress, ignoring duplicate call');
            return;
        }

        try {
            setIsGenerating(true);
            setGenerationState('generating');
            setProgress(0);
            
            // Step 1: Validate data
            setCurrentStep('Validando datos fiscales...');
            setProgress(10);
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Step 2: Prepare API request
            setCurrentStep('Preparando datos para el timbrado...');
            setProgress(25);
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Step 3: Call real API for invoice generation
            setCurrentStep('Enviando al PAC para timbrado...');
            setProgress(50);
            
            // Use the real API service instead of mock
            const apiResult = await generateInvoiceAPI(sale, customer);
            
            if (!apiResult.success) {
                // Handle duplicate invoice error specifically
                if (apiResult.isDuplicate) {
                    const duplicateError = new Error(apiResult.error);
                    duplicateError.isDuplicate = true;
                    duplicateError.existingInvoice = apiResult.existingInvoice;
                    throw duplicateError;
                }
                
                // Store both error message and detailed error if available
                const error = new Error(apiResult.error);
                error.detailedError = apiResult.detailedError || null;
                throw error;
            }
            
            // Step 4: Process API response  
            setCurrentStep('Procesando respuesta del PAC...');
            setProgress(75);
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Step 5: Generate download links
            setCurrentStep('Generando enlaces de descarga...');
            setProgress(90);
            await new Promise(resolve => setTimeout(resolve, 300));
            
            // Step 6: Complete
            setCurrentStep('¡Factura generada exitosamente!');
            setProgress(100);
            await new Promise(resolve => setTimeout(resolve, 200));
            
            setGeneratedInvoice(apiResult.data);
            setGenerationState('completed');
            
        } catch (error) {
            console.error('Error generating invoice:', error);
            setGenerationState('error');
            
            // Special handling for duplicate invoices
            if (error.isDuplicate) {
                setCurrentStep(`Esta venta ya fue facturada: ${error.message || 'Error de duplicado'}`);
                // For duplicate errors, don't allow retries as it won't work
                setRetryCount(maxRetries);
            } else {
                setCurrentStep(`Error: ${error.message || 'Error desconocido'}`);
            }
            
            // Store detailed error if available
            if (error.detailedError) {
                setDetailedError(error.detailedError);
            }
            
            // Don't call onError immediately, let user decide to retry
        } finally {
            setIsGenerating(false);
        }
    }, [sale, customer, isGenerating]);

    // REMOVED: Auto-start useEffect that was causing duplicate calls
    // Now generation is triggered manually by user clicking a button

    const handleRetry = async () => {
        if (retryCount >= maxRetries) {
            onError(`No se pudo generar la factura después de ${maxRetries} intentos. Por favor contacte soporte.`);
            return;
        }
        
        // Reset error state
        setDetailedError(null);
        setRetryCount(prev => prev + 1);
        await generateInvoice();
    };

    const handleContinue = () => {
        if (generatedInvoice) {
            onInvoiceGenerated(generatedInvoice);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount);
    };

    const renderProgressBar = () => (
        <div className="progress-container">
            <div className="progress-bar">
                <div 
                    className="progress-fill"
                    style={{ width: `${progress}%` }}
                ></div>
            </div>
            <div className="progress-text">
                {currentStep} ({progress}%)
            </div>
        </div>
    );

    const renderGenerationStatus = () => {
        switch (generationState) {
            case 'ready':
                return (
                    <div className="generation-ready">
                        
                        <h3>Listo para generar su factura CFDI 📄</h3>
                        <p>Hemos validado sus datos y estamos listos para proceder con el timbrado</p>
                        
                        <div className="generation-preview">
                            <h4>Resumen a facturar:</h4>
                            <div className="preview-details">
                                <div className="detail-row">
                                    <span className="label">Cliente:</span>
                                    <span className="value">{customer.razonSocial}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">RFC:</span>
                                    <span className="value">{customer.rfc}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Total:</span>
                                    <span className="value total">{formatCurrency(sale.total)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="label">Productos:</span>
                                    <span className="value">{sale.productos.length} artículo(s)</span>
                                </div>
                            </div>
                        </div>

                        <div className="form-actions">
                            
                            <button
                                onClick={onGoBack}
                                className="btn-secondary"
                            >
                                ← Regresar
                            </button>

                            <button
                                onClick={generateInvoice}
                                className="btn-primary"
                                disabled={isGenerating}
                            >
                                {isGenerating ? 'Generando...' : 'Generar Factura CFDI'}
                            </button>
                        </div>
                        
                        <div className="compliance-notice">
                            <h4>🔒 Proceso de Timbrado CFDI 4.0</h4>
                            <p>
                                Al hacer clic en "Generar Factura", su documento será enviado al
                                Proveedor Autorizado de Certificación (PAC) para el timbrado oficial.
                                Este proceso es irreversible una vez completado.
                            </p>
                        </div>
                    </div>
                );

            case 'generating':
                return (
                    <div className="generation-in-progress">
                        <div className="spinner"></div>
                        <h3>Generando su factura CFDI</h3>
                        <p>Por favor espere mientras procesamos su solicitud...</p>
                        {renderProgressBar()}
                        <div className="technical-info">
                            <small>
                                Generando factura electrónica conforme a CFDI 4.0
                                <br />
                                Este proceso puede tardar unos momentos
                            </small>
                        </div>
                    </div>
                );

            case 'error':
                return (
                    <div className="generation-error">
                        <div className="error-icon">⚠️</div>
                        <h3>Error en la generación</h3>
                        <p>{currentStep}</p>
                        
                        <div className="error-actions">
                            {retryCount < maxRetries ? (
                                <button
                                    onClick={handleRetry}
                                    className="btn-primary"
                                    disabled={isGenerating}
                                >
                                    {isGenerating ? 'Reintentando...' : `Reintentar (${retryCount + 1}/${maxRetries})`}
                                </button>
                            ) : (
                                <p className="max-retries">
                                    Se agotaron los reintentos. Por favor contacte soporte técnico.
                                </p>
                            )}
                            
                            <button
                                onClick={onGoBack}
                                className="btn-secondary"
                                disabled={isGenerating}
                            >
                                ← Regresar
                            </button>
                        </div>
                        
                        {detailedError && (
                            <div className="technical-details error-box">
                                <h4>❌ <strong>Detalles técnicos</strong></h4>
                                <div className="error-details">
                                    <p><strong>Error:</strong> {currentStep}</p>
                                    <p><strong>Detalles:</strong> {detailedError}</p>
                                    <p><strong>Intento:</strong> {retryCount + 1}</p>
                                </div>
                            </div>
                        )}
                        
                        {!detailedError && (
                            <div className="technical-details">
                                <details>
                                    <summary>Detalles técnicos</summary>
                                    <div className="error-details">
                                        <p><strong>Intento:</strong> {retryCount + 1}</p>
                                        <p><strong>Paso fallido:</strong> {currentStep}</p>
                                        <p><strong>Recomendación:</strong> Verifique su conexión a internet y reintente</p>
                                    </div>
                                </details>
                            </div>
                        )}
                    </div>
                );

            case 'completed':
                return (
                    <div className="generation-success">
                        <div className="success-icon">✅</div>
                        <h3>¡Factura generada exitosamente!</h3>
                        <p>Su factura CFDI ha sido timbrada y está lista para su entrega</p>
                        
                        {generatedInvoice && (
                            <div className="invoice-summary">
                                <h4>Resumen de la Factura</h4>
                                <div className="summary-details">
                                    <div className="detail-row">
                                        <span className="label">UUID Fiscal:</span>
                                        <span className="value uuid">
                                            {generatedInvoice.factura?.uuid || generatedInvoice.uuid}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Fecha de Timbrado:</span>
                                        <span className="value">
                                            {new Date(generatedInvoice.factura?.fecha_timbrado || generatedInvoice.fechaTimbrado).toLocaleString('es-MX')}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Total:</span>
                                        <span className="value total">
                                            {formatCurrency(generatedInvoice.factura?.total || generatedInvoice.total)}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">RFC Emisor:</span>
                                        <span className="value">MAN123456789</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">RFC Receptor:</span>
                                        <span className="value">
                                            {generatedInvoice.cliente?.rfc || generatedInvoice.customer?.rfc}
                                        </span>
                                    </div>
                                    {generatedInvoice.factura?.serie && (
                                        <div className="detail-row">
                                            <span className="label">Serie-Folio:</span>
                                            <span className="value">
                                                {generatedInvoice.factura.serie}-{generatedInvoice.factura.folio}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="next-steps">
                            <h4>Siguientes pasos:</h4>
                            <ol>
                                <li>Confirme los datos de entrega</li>
                                <li>Seleccione cómo desea recibir su factura</li>
                                <li>Descargue o reciba por correo electrónico</li>
                            </ol>
                        </div>

                        <div className="success-actions">
                            <button
                                onClick={handleContinue}
                                className="btn-primary"
                            >
                                Continuar con entrega →
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="invoice-generator">
            <div className="generator-header">
                <h2>Generación de Factura CFDI</h2>
                <p>Procesando su solicitud de facturación electrónica</p>
            </div>

            <div className="generator-content">
                {/* Sale Summary */}
                <div className="sale-summary-card">
                    <h3>Datos de la Venta</h3>
                    <div className="summary-grid">
                        <div className="summary-item">
                            <span className="label">Fecha:</span>
                            <span className="value">
                                {new Date(sale.fechaVenta).toLocaleDateString('es-MX')}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="label">Sucursal:</span>
                            <span className="value">{sale.sucursalName}</span>
                        </div>
                        <div className="summary-item">
                            <span className="label">Total:</span>
                            <span className="value">{formatCurrency(sale.total)}</span>
                        </div>
                        <div className="summary-item">
                            <span className="label">Cliente:</span>
                            <span className="value">{customer.razonSocial}</span>
                        </div>
                    </div>
                </div>

                {/* Generation Status */}
                <div className="generation-status-card">
                    {renderGenerationStatus()}
                </div>

                {/* CFDI Compliance Notice */}
                <div className="compliance-notice">
                    <h4>🔒 Certificación CFDI 4.0</h4>
                    <p>
                        Esta factura cumple con todas las normativas fiscales mexicanas vigentes.
                        El proceso de timbrado se realiza a través de un Proveedor Autorizado de
                        Certificación (PAC) reconocido por el SAT.
                    </p>
                </div>
            </div>
        </div>
    );
};

InvoiceGenerator.propTypes = {
    sale: PropTypes.shape({
        id: PropTypes.number.isRequired,
        sucursalId: PropTypes.number.isRequired,
        fechaVenta: PropTypes.string.isRequired,
        total: PropTypes.number.isRequired,
        productos: PropTypes.array.isRequired
    }).isRequired,
    customer: PropTypes.shape({
        rfc: PropTypes.string.isRequired,
        razonSocial: PropTypes.string.isRequired,
        email: PropTypes.string,
        direccion: PropTypes.object.isRequired
    }).isRequired,
    onInvoiceGenerated: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onGoBack: PropTypes.func.isRequired
};

export default InvoiceGenerator;